import React, { useMemo } from 'react';
import { Button } from '@grofers/dock-design-kit';
import ProductCard from './ProductCard';
import ItemPickerDropdown from './ItemPickerDropdown';
import { Skeleton } from '../../../ComponentsV1/Skeleton/Skeleton';

const toProductCardProps = (item, count) => ({
  id: item.id,
  productId: item.product_id,
  imageUrl: item.item_image_url || null,
  name: item.text,
  quantityLabel: item.description || '',
  count,
  maxCount: item.max_quantity,
});

/**
 * Content of accordion step 1 - "Order & Product Confirmation".
 * `items` is the raw `multi_select_range.source` array from the
 * `copilot/menu` API - items already flagged `is_selected` render as
 * cards up front; the rest are offered via the "Add more items from
 * order" picker.
 *
 * `counts`/`onCountsChange` are controlled by CopilotView rather than owned
 * here - AccordionStep unmounts this component while its step is collapsed,
 * so local state would reset every time the agent closed step 1 (to look at
 * a later step) and reopened it.
 */
const OrderProductConfirmation = ({
  items = [],
  counts = {},
  onCountsChange = () => {},
  onConfirm = () => {},
  isLoading = false,
}) => {
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items],
  );

  const selectedProducts = items
    .filter((item) => (counts[item.id] || 0) > 0)
    .map((item) => toProductCardProps(item, counts[item.id]));

  const poolItems = items.filter((item) => (counts[item.id] || 0) === 0);
  const poolCounts = Object.fromEntries(poolItems.map((item) => [item.id, 0]));

  const updateCount = (id, delta) => {
    const item = itemsById.get(id);
    const max = item?.max_quantity ?? Infinity;
    const next = Math.min(max, Math.max(1, (counts[id] ?? 1) + delta));
    onCountsChange({ ...counts, [id]: next });
  };

  const removeProduct = (id) => {
    onCountsChange({ ...counts, [id]: 0 });
  };

  const handleApplyPicked = (newCounts) => {
    onCountsChange({ ...counts, ...newCounts });
  };

  return (
    <div className='tw-flex tw-flex-col tw-w-full tw-mt-4'>
      <div className='tw-flex tw-flex-col tw-gap-[16px] tw-w-full'>
        <p className='tw-text-[12px] tw-leading-tight tw-font-regular tw-text-grey-800 tw-m-0 tw-w-full'>
          Verify the issue with the customer and select the affected products
          &amp; their quantity below
        </p>

        <div className='tw-w-full tw-overflow-hidden tw-border-2 tw-border-solid tw-border-[#feee95] tw-flex tw-flex-col tw-rounded-xl'>
          <div className='tw-flex tw-flex-col tw-w-full'>
            <div className='tw-w-full tw-bg-[#feee95] tw-p-3 tw-rounded-t-lg'>
              <p className='tw-text-[12px] tw-leading-none tw-font-medium tw-text-grey-900'>
                Customer’s Selection
              </p>
            </div>
            {isLoading ? (
              <div className='tw-p-3'>
                <Skeleton
                  className='tw-gap-2'
                  rows={[
                    { cols: 1, height: '5rem' },
                    { cols: 1, height: '5rem' },
                  ]}
                />
              </div>
            ) : selectedProducts.length > 0 ? (
              selectedProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  imageUrl={product.imageUrl}
                  name={product.name}
                  quantityLabel={product.quantityLabel}
                  count={product.count}
                  onIncrement={() => updateCount(product.id, 1)}
                  onDecrement={() => updateCount(product.id, -1)}
                  onRemove={() => removeProduct(product.id)}
                />
              ))
            ) : (
              <p className='tw-text-[12px] tw-text-grey-600 tw-m-0 tw-p-3'>
                No items selected
              </p>
            )}
          </div>
        </div>

        {/* Add more items from order */}
        <div className='tw-flex tw-flex-col tw-gap-[6px] tw-w-full'>
          <p className='tw-text-[12px] tw-leading-tight tw-font-regular tw-text-grey-800 tw-m-0 tw-w-full'>
            Add more items from order
          </p>

          {isLoading ? (
            <Skeleton
              className='tw-gap-2'
              rows={[{ cols: 1, height: '4rem' }]}
            />
          ) : (
            <ItemPickerDropdown
              items={poolItems}
              selectedCounts={poolCounts}
              onApply={handleApplyPicked}
            />
          )}
        </div>
      </div>

      <div className='tw-flex tw-justify-end tw-gap-[12px] tw-w-full tw-pt-4'>
        {isLoading ? (
          <div className='tw-w-1/2'>
            <Skeleton
              className='tw-gap-2'
              rows={[{ cols: 1, height: '4rem' }]}
            />
          </div>
        ) : (
          <Button
            variant='primary'
            buttonType='default'
            size='md'
            shape='square'
            disabled={selectedProducts.length === 0}
            onClick={() => onConfirm(selectedProducts)}
            className={selectedProducts.length > 0 ? '!tw-bg-base-green' : ''}
          >
            Confirm
          </Button>
        )}
      </div>
    </div>
  );
};

export default OrderProductConfirmation;
